import { type ApolloError } from '@apollo/client';
import {
  Breakpoints,
  Button,
  ButtonColor,
  compact,
  IconChevronDown,
  useMediaQuery,
} from '@leland-dev/leland-ui-library';
import { useRouter } from 'next/router';
import React, {
  type Dispatch,
  type FC,
  type RefObject,
  type SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';

import {
  CoachSortBy,
  SortOrder,
} from '../../__generated-gql-types__/globalTypes';
import mxpnl from '../../utils/analytics/mixpanel';
import {
  formatCoachSortBy,
  formatSortingFilterLabel,
  formatSortOrder,
} from '../../utils/formatters';
import { type SearchFiltersVariables } from '../../utils/search';
import { getUrlObject } from '../../utils/url';
import ActionsMenu from '../common/ActionsMenu';
import Pagination from '../common/Pagination';
import { type SchoolFilterHookOutput } from '../filters/SchoolFilters';

import CoachList from './CoachList';
import MobileSearchFilters from './MobileSearchFilters';
import MobileSortingFilter from './MobileSortingFilter';
import SearchFilters from './SearchFilters';
import { TalkToATeamMemberButton } from './TalkToATeamMemberButton';
import {
  type CoachesSectionWithFilterCategoryFragment,
  type CoachesSectionWithFilterCoachFragment,
  type CoachesSectionWithFilterLelandPlusContributorCoachBatchResponseFragment,
  type CoachesSectionWithFilterSkuFragment,
} from './__generated-gql-types__/CoachesSectionWithFilters.generated';

export const SRP_PAGE_SIZE = 10;

const DEFAULT_SORTING_FILTER_OPTION = {
  value: '__DEFAULT__',
  label: 'Default',
};

interface BaseTrackingVars {
  categorySlug: string;
  subCategorySlug?: string;
}

interface CoachesSectionWithFiltersProps {
  title: string;
  coaches: CoachesSectionWithFilterCoachFragment[];
  totalCoaches: number;
  category: Possible<CoachesSectionWithFilterCategoryFragment>;
  subCategorySlug?: Possible<string>;
  schoolFilters: SchoolFilterHookOutput;
  searchVariables: SearchFiltersVariables;
  setSearchVariables: Dispatch<SetStateAction<SearchFiltersVariables>>;
  resetVariables: () => void;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  buildPaginationUrlString: (page: Possible<number>) => string;
  isSearching?: boolean;
  isDirty?: boolean;
  isDirtyIgnoreSubcategory?: boolean;
  hasRealFilters?: boolean;
  loading?: boolean;
  error?: ApolloError;
  innerRef?: RefObject<Nullable<HTMLDivElement>>;
  baseTrackingVars?: BaseTrackingVars;
  trackingVarsWithSearchVars?: SearchFiltersVariables;
  spacingClassName?: string;
  hidePrice: boolean;
  skusData: CoachesSectionWithFilterSkuFragment[];
}

type CoachesSectionWithFiltersLelandPlusBannerProps =
  | {
      showLelandPlusBanner?: never;
      lelandPlusContributors?: never;
    }
  | {
      showLelandPlusBanner: boolean;
      lelandPlusContributors: Possible<CoachesSectionWithFilterLelandPlusContributorCoachBatchResponseFragment>;
    };

export const CoachesSectionWithFilters: FC<
  CoachesSectionWithFiltersProps &
    CoachesSectionWithFiltersLelandPlusBannerProps
> = ({
  title,
  coaches,
  totalCoaches,
  category,
  subCategorySlug,
  schoolFilters,
  searchVariables,
  setSearchVariables,
  resetVariables,
  page,
  setPage,
  buildPaginationUrlString,
  isSearching,
  isDirty = false,
  isDirtyIgnoreSubcategory = false,
  hasRealFilters = false,
  loading = false,
  error,
  innerRef,
  baseTrackingVars,
  trackingVarsWithSearchVars,
  spacingClassName = '',
  showLelandPlusBanner,
  lelandPlusContributors,
  hidePrice,
  skusData,
}) => {
  const router = useRouter();
  const isSm = useMediaQuery(Breakpoints.SM);

  const [selectedSortOption, setSelectedSortOption] = useState(
    DEFAULT_SORTING_FILTER_OPTION.value,
  );

  const onSortingFilterClick = useCallback(
    (value: string) => {
      setSelectedSortOption(value);

      let sortBy: SearchFiltersVariables['sortBy'];
      let sortOrder: SearchFiltersVariables['sortOrder'];

      if (value === DEFAULT_SORTING_FILTER_OPTION.value) {
        sortBy = null;
        sortOrder = null;
      } else if (Object.values(SortOrder).includes(value as SortOrder)) {
        sortBy = CoachSortBy.HOURLY_RATE;
        sortOrder = value as SortOrder;
      } else if (Object.values(CoachSortBy).includes(value as CoachSortBy)) {
        sortBy = value as CoachSortBy;
        sortOrder = null;
      }

      setSearchVariables((oldVars) => ({ ...oldVars, sortBy, sortOrder }));

      mxpnl.track('SRP - click - Sort', {
        ...baseTrackingVars,
        sortBy,
        sortOrder,
      });
    },
    [setSearchVariables, baseTrackingVars],
  );

  const SORTING_FILTER_ACTIONS = useMemo(
    () =>
      [
        {
          value: DEFAULT_SORTING_FILTER_OPTION.value,
          label: DEFAULT_SORTING_FILTER_OPTION.label,
          onClick: () =>
            onSortingFilterClick(DEFAULT_SORTING_FILTER_OPTION.value),
        },
        {
          value: CoachSortBy.REVIEW_RATE,
          label: formatCoachSortBy(CoachSortBy.REVIEW_RATE),
          onClick: () => onSortingFilterClick(CoachSortBy.REVIEW_RATE),
        },
        ...(hidePrice
          ? []
          : [
              {
                value: SortOrder.ASCENDING,
                label: formatSortOrder(SortOrder.ASCENDING),
                onClick: () => onSortingFilterClick(SortOrder.ASCENDING),
              },
              {
                value: SortOrder.DESCENDING,
                label: formatSortOrder(SortOrder.DESCENDING),
                onClick: () => onSortingFilterClick(SortOrder.DESCENDING),
              },
            ]),
        {
          value: CoachSortBy.REVIEW_NUMBER,
          label: formatCoachSortBy(CoachSortBy.REVIEW_NUMBER),
          onClick: () => onSortingFilterClick(CoachSortBy.REVIEW_NUMBER),
        },
      ].filter(compact),
    [hidePrice, onSortingFilterClick],
  );

  const selectedSortingFilterLabel = useMemo(() => {
    if (
      searchVariables.sortBy === CoachSortBy.RELEVANCE ||
      (!searchVariables.sortOrder && !searchVariables.sortBy)
    ) {
      return DEFAULT_SORTING_FILTER_OPTION.label.toLowerCase();
    }

    return (
      SORTING_FILTER_ACTIONS.find(
        (action) =>
          (action.value as SortOrder) === searchVariables.sortOrder ||
          (action.value as CoachSortBy) === searchVariables.sortBy,
      )?.label ?? DEFAULT_SORTING_FILTER_OPTION.label
    ).toLowerCase();
  }, [
    SORTING_FILTER_ACTIONS,
    searchVariables.sortBy,
    searchVariables.sortOrder,
  ]);

  const selectedSchoolIds = schoolFilters.selectedSchools.map(
    (school) => school.id,
  );
  const selectedCompanyIds = searchVariables.companyFilter ?? [];

  return (
    <div ref={innerRef} className={spacingClassName}>
      <div className="mb-4 flex items-end justify-between lg:mb-6">
        <h2 className="text-2xl font-medium text-leland-gray-dark">{title}</h2>
        <div className="hidden flex-col items-end lg:flex">
          <ActionsMenu
            actions={SORTING_FILTER_ACTIONS}
            CustomButton={({ onClick }) => {
              return (
                <div className="flex min-w-60 justify-end">
                  <Button
                    label={`Sort by ${selectedSortingFilterLabel}`}
                    RightIcon={IconChevronDown}
                    buttonColor={ButtonColor.GRAY}
                    rounded
                    onClick={onClick}
                  />
                </div>
              );
            }}
          />
        </div>
      </div>
      <div className="mb-6 grid grid-cols-1 gap-2 lg:hidden">
        <TalkToATeamMemberButton
          buttonId="CoachesSectionWithFilters"
          className={isSearching ? '' : 'hidden'}
        />
        <div className="grid w-full grid-cols-2 gap-2">
          <MobileSearchFilters
            hidePrice={hidePrice}
            isDirty={isDirty}
            category={category}
            searchVariables={searchVariables}
            setSearchVariables={setSearchVariables}
            resetVariables={resetVariables}
            schoolFilters={schoolFilters}
            skusData={skusData}
          />
          <MobileSortingFilter
            options={SORTING_FILTER_ACTIONS.map((action) => {
              return {
                ...action,
                label: formatSortingFilterLabel(action.label),
              };
            })}
            selectedOption={selectedSortOption}
          />
        </div>
      </div>
      <div className="flex border-t">
        <SearchFilters
          hidePrice={hidePrice}
          className="hidden w-66 shrink-0 lg:block"
          headerClassName="h-9"
          isDirty={isDirty}
          category={category}
          searchVariables={searchVariables}
          setSearchVariables={setSearchVariables}
          resetVariables={resetVariables}
          schoolFilters={schoolFilters}
          skusData={skusData}
        />
        <div className="flex w-full grow flex-col overflow-hidden lg:ml-10">
          <CoachList
            hidePrice={hidePrice}
            coaches={coaches}
            category={category}
            subCategorySlug={subCategorySlug}
            selectedSchoolIds={selectedSchoolIds}
            selectedCompanyIds={selectedCompanyIds}
            loading={loading}
            error={error}
            resetVariables={resetVariables}
            onClick={(c, index) =>
              mxpnl.track('SRP - click - Coach Card', {
                ...trackingVarsWithSearchVars,
                index,
                overallIndex: (page - 1) * SRP_PAGE_SIZE + index,
                page,
                coachSlug: c.slug,
                coachId: c.id,
              })
            }
            showLelandPlusBanner={
              !!showLelandPlusBanner && !isDirtyIgnoreSubcategory
            }
            lelandPlusContributors={lelandPlusContributors}
            skusData={skusData}
          />
          <div className="mt-8 flex items-center justify-center lg:justify-start">
            <Pagination
              currentPage={page}
              totalElements={totalCoaches}
              pageSize={SRP_PAGE_SIZE}
              buildUrl={(page) => {
                const { category, goal, subCategoryAndPage, ...rest } =
                  router.query;
                return getUrlObject(buildPaginationUrlString(page), {
                  ...rest,
                });
              }}
              scrollRoot={innerRef}
              maxPageItems={isSm ? 5 : 3}
              onClick={(newPage, clickedOnNumber) => {
                setPage(newPage);
                mxpnl.track('SRP - click - Coach Pagination', {
                  ...trackingVarsWithSearchVars,
                  newPage,
                  clickedOnNumber,
                });
              }}
              useShallowLink={hasRealFilters}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
